import React from 'react';
import { motion } from 'framer-motion';
import { 
  FiSettings, FiDollarSign, FiBarChart2, FiZap
} from 'react-icons/fi';
import { RiPlantLine } from 'react-icons/ri';

import { TabNavProps } from '../../types/components';

/**
 * TabNav component for navigation between dashboard tabs
 * 
 * This component provides a tab navigation interface with animated indicators,
 * icons, and hover effects.
 */
const TabNav: React.FC<TabNavProps> = ({ tabs, activeTab, onChange }) => {
  /**
   * Get icon component based on tab ID
   */
  const getTabIcon = (tabId: string) => {
    switch (tabId) {
      case 'inputs':
        return <FiSettings className="mr-2" />;
      case 'comparison':
        return <FiDollarSign className="mr-2" />;
      case 'financial':
        return <FiBarChart2 className="mr-2" />;
      case 'energy':
        return <FiZap className="mr-2" />;
      case 'emissions':
        return <RiPlantLine className="mr-2" />;
      default:
        return null;
    }
  };
  
  return (
    <div className="modern-tabs mb-6 p-1.5 bg-gray-100 rounded-xl shadow-sm">
      <nav 
        className="flex justify-around" 
        role="tablist" 
        aria-label="Dashboard tabs"
      >
        {tabs.map(tab => (
          <motion.button
            key={tab.id}
            onClick={() => onChange(tab.id)}
            className={`
              modern-tab ${activeTab === tab.id ? 'active' : ''}
              flex items-center whitespace-nowrap py-3 px-4 rounded-lg font-medium text-sm
            `}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            role="tab"
            id={`tab-${tab.id}`}
            aria-selected={activeTab === tab.id}
            aria-controls={`panel-${tab.id}`}
            tabIndex={activeTab === tab.id ? 0 : -1}
          >
            {getTabIcon(tab.id)}
            {tab.name}
          </motion.button>
        ))}
      </nav>
    </div>
  );
};

export default TabNav;